/// <amd-module name="Repos/Views/ProjectPolicies/Services/ProjectPoliciesService" />
import { IProjectData, ITfsPageService } from "Tfs/Platform/Page";
import { IReadonlyObservableArray, IReadonlyObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { IVssPageContext, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { MakeOptional } from "VSS/Core/Util/Types";
import {
    IGetExistingBranchHintsRequestParameters,
    IGetExistingBranchHintsResult,
    IProjectPoliciesResult,
    IProjectPoliciesService,
    IProjectPolicyItem
} from "../Contracts";

const ProjectPoliciesContributionId = "ms.vss-code-web.admin-project-policies-data-provider";
const ExistingBranchesContributionId = "ms.vss-code-web.admin-project-policies-branches-data-provider";
const PolicyRemovalContributionId = "ms.vss-code-web.admin-project-policies-removal-data-provider";

class ProjectPoliciesService extends VssService implements IProjectPoliciesService {
    private _project = new ObservableValue<IProjectData | undefined>(undefined);
    public readonly project: IReadonlyObservableValue<IProjectData | undefined> = this._project;

    private _hasEditPermission = new ObservableValue<boolean>(false);
    public readonly hasEditPermission: IReadonlyObservableValue<boolean> = this._hasEditPermission;

    private _repositoryCount = new ObservableValue<number>(0);
    public readonly repositoryCount: IReadonlyObservableValue<number> = this._repositoryCount;

    private _projectPoliciesLoading = new ObservableValue<boolean>(true);
    public readonly projectPoliciesLoading: IReadonlyObservableValue<boolean> = this._projectPoliciesLoading;

    private _projectPolicies = new ObservableArray<IProjectPolicyItem>();
    public readonly projectPolicies: IReadonlyObservableArray<IProjectPolicyItem> = this._projectPolicies;

    private _existingBranchHints = new ObservableValue<IGetExistingBranchHintsResult | undefined>(undefined);
    public readonly existingBranchHints: IReadonlyObservableValue<IGetExistingBranchHintsResult | undefined> = this._existingBranchHints;

    private contributionService: IVssContributionService;

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);

        const tfsPageService = pageContext.getService<ITfsPageService>("ITfsPageService");
        this._project.value = tfsPageService.getData()!.project;

        this.contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");

        this.forceReloadProjectPolicies(false);
    }

    public async reloadProjectPoliciesAsync(): Promise<void> {
        if (!this._projectPoliciesLoading.value) {
            await this.forceReloadProjectPolicies(true);
        }
    }

    private async forceReloadProjectPolicies(ignoreCache: boolean): Promise<void> {
        this._projectPoliciesLoading.value = true;

        try {
            const result = await this.contributionService.getDataAsync<IProjectPoliciesResult>(ProjectPoliciesContributionId, {}, ignoreCache);

            const policies = (result && result.projectPolicies) || [];

            policies.sort(ProjectPoliciesService.sortByScope);

            this._projectPolicies.splice(0, this._projectPolicies.length, ...policies);

            this._hasEditPermission.value = !!(result && !result.readonlyMode);
            this._repositoryCount.value = (result && result.repositoryCount) || 0;

            this._projectPoliciesLoading.value = false;
        } catch (ex) {
            this._projectPolicies.removeAll();
            this._repositoryCount.value = 0;
            this._projectPoliciesLoading.value = false;
        }
    }

    public async deleteAllPoliciesForScopeAsync(scope: string): Promise<void> {
        if (!scope || this._projectPoliciesLoading.value) {
            return;
        }

        this._projectPoliciesLoading.value = true;

        const requestParameters = { scope };

        try {
            const result = await this.contributionService.getDataAsync<boolean>(PolicyRemovalContributionId, requestParameters, true);
        } finally {
            // This will set _projectPoliciesLoading = false when it completes
            await this.forceReloadProjectPolicies(true);
        }
    }

    public async getExistingBranchHintsAsync(branchPattern: string): Promise<void> {
        this._existingBranchHints.value = undefined;

        const requestParameters: IGetExistingBranchHintsRequestParameters = {
            branchPattern: branchPattern,
            top: 10
        };

        const result = await this.contributionService.getDataAsync<IGetExistingBranchHintsResult>(
            ExistingBranchesContributionId,
            requestParameters
        );

        this._existingBranchHints.value = { branchPattern, ...(result! as MakeOptional<IGetExistingBranchHintsResult, "branchPattern">) };
    }

    // Simple sort for presentation -- first "every branch" policies, then default-branch policies, then just alphabetically.
    // It would maybe be nice to put this in UI code rather than a service. On the other hand, I like the fact that the service
    // only makes these lists available as ReadOnly observable arrays, so the consumer can't really sort them.
    private static sortByScope = (a: IProjectPolicyItem, b: IProjectPolicyItem): number => {
        // This simplified logic works because scopes are unique -- i.e., you'll never have a === b

        if (a.scope === "*:refs/heads/*") {
            return -1;
        }
        if (b.scope === "*:refs/heads/*") {
            return +1;
        }

        if (a.scope === "*:~default") {
            return -1;
        }
        if (b.scope === "*:~default") {
            return +1;
        }

        return localeIgnoreCaseComparer(a.scope, b.scope);
    };
}

Services.add("IProjectPoliciesService", { serviceFactory: ProjectPoliciesService });
